import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  sendChatMessage,
  uploadChatFile,
  type ChatResultItem,
  type ChatCard,
  type ChatBreakdown,
  type ChatHistoryItem,
} from '../lib/chatApi';

type ChatMessage = {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  results?: ChatResultItem[];
  cards?: ChatCard[];
  breakdown?: ChatBreakdown[];
  ts: number;
};

const STORAGE_KEY = 'usc-copilot-chat-v1';
const MAX_HISTORY = 50;

const WELCOME: ChatMessage = {
  id: 'welcome',
  role: 'assistant',
  text: "Hi! I'm Aquamaris Copilot — your AI assistant for everything in the system.\n\nI can find records, give you stats, navigate to anywhere, and more.",
  ts: Date.now(),
};

const SUGGESTED_PROMPTS = [
  { label: '📊 Today summary',         text: 'Today summary' },
  { label: '⚠️ Overdue payables',     text: 'Show me overdue payables' },
  { label: '📥 Recent RFQs',           text: 'Recent RFQs' },
  { label: '🏷️ Low stock items',       text: 'Inventory low on stock' },
  { label: '💰 Receivables status',    text: 'Receivables summary' },
  { label: '🐟 Who supplies shrimp',   text: 'Who supplies shrimp' },
];

const TYPE_ICONS: Record<string, string> = {
  rfq:              '📥',
  quotation:        '📄',
  confirmed_order:  '✅',
  vendor_invoice:   '🧾',
  client_invoice:   '💳',
  vendor:           '🏭',
  client:           '👤',
  inventory:        '📦',
  email:            '✉️',
  vendor_po:        '📋',
  payable:          '💸',
};

function loadFromStorage(): ChatMessage[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [WELCOME];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length) return parsed.slice(-MAX_HISTORY);
    return [WELCOME];
  } catch {
    return [WELCOME];
  }
}

function saveToStorage(messages: ChatMessage[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(messages.slice(-MAX_HISTORY)));
  } catch {
    /* ignore quota errors */
  }
}

export const FloatingAssistant: React.FC = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [input, setInput] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>(loadFromStorage);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Persist on every message change
  useEffect(() => { saveToStorage(messages); }, [messages]);

  // Auto-scroll on new message
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, loading, open]);

  // Keyboard shortcut: Ctrl/Cmd + K to toggle
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
      if (e.key === 'Escape' && open) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Close on click outside the widget
  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  const canSend = useMemo(() => !!input.trim() || !!file, [input, file]);

  const buildHistoryPayload = (): ChatHistoryItem[] =>
    messages.slice(-8).map((m) => ({ role: m.role, text: m.text }));

  const runQuery = async (text: string, attachedFile: File | null = null) => {
    if (loading) return;
    const stamp = Date.now();
    const userMessage: ChatMessage = {
      id: `user-${stamp}`,
      role: 'user',
      text: text || (attachedFile ? `📎 ${attachedFile.name}` : ''),
      ts: stamp,
    };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = attachedFile
        ? await uploadChatFile(text, attachedFile)
        : await sendChatMessage(text, buildHistoryPayload());

      const assistantMessage: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        text: response.reply || 'Done.',
        results: response.results || [],
        cards: response.cards || [],
        breakdown: response.breakdown || [],
        ts: Date.now(),
      };
      setMessages((prev) => [...prev, assistantMessage]);
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
    } catch (err: any) {
      setMessages((prev) => [...prev, {
        id: `err-${Date.now()}`, role: 'assistant',
        text: `❌ ${err?.message || 'Something went wrong.'}`,
        ts: Date.now(),
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = () => {
    if (!canSend || loading) return;
    runQuery(input.trim(), file);
  };

  const handleQuickPrompt = (text: string) => {
    if (loading) return;
    runQuery(text, null);
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleResultClick = (link?: string) => {
    if (!link) return;
    navigate(link);
    setOpen(false);
  };

  const handleClearChat = () => {
    if (!window.confirm('Clear conversation history?')) return;
    setMessages([WELCOME]);
  };

  return (
    <div ref={containerRef} className="fixed bottom-6 right-6 z-50">
      {open ? (
        <div className="w-[min(420px,calc(100vw-3rem))] h-[min(600px,calc(100vh-6rem))] bg-white border border-slate-200 shadow-2xl rounded-2xl flex flex-col overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 bg-gradient-to-r from-[#085489] to-[#0A6AAC] text-white flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-white/20 flex items-center justify-center text-base">🤖</div>
              <div>
                <div className="text-sm font-bold leading-tight">Aquamaris Copilot</div>
                <div className="text-[10px] text-white/70 leading-tight">Ask me anything · ⌘K to toggle</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={handleClearChat}
                className="text-[10px] px-2 py-1 rounded bg-white/10 hover:bg-white/20 text-white"
                title="Clear conversation"
              >
                Clear
              </button>
              <button
                onClick={() => setOpen(false)}
                className="h-7 w-7 rounded bg-white/10 hover:bg-white/20 text-white text-sm"
                title="Close (Esc)"
              >
                ×
              </button>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-auto px-4 py-3 space-y-3 bg-slate-50">
            {messages.map((msg) => (
              <MessageBubble key={msg.id} msg={msg} onResultClick={handleResultClick} />
            ))}
            {loading && (
              <div className="text-left">
                <div className="inline-flex items-center gap-2 bg-white text-slate-500 text-xs px-3 py-2 rounded-2xl border border-slate-200">
                  <span className="h-2 w-2 bg-[#0A6AAC] rounded-full animate-pulse" />
                  <span className="h-2 w-2 bg-[#0A6AAC] rounded-full animate-pulse" style={{ animationDelay: '0.2s' }} />
                  <span className="h-2 w-2 bg-[#0A6AAC] rounded-full animate-pulse" style={{ animationDelay: '0.4s' }} />
                  <span className="ml-1">Thinking...</span>
                </div>
              </div>
            )}
          </div>

          {/* Suggested prompts — only show when there's just the welcome message */}
          {messages.length <= 1 && (
            <div className="px-3 py-2 border-t border-slate-100 bg-white">
              <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5">Try asking</div>
              <div className="flex flex-wrap gap-1.5">
                {SUGGESTED_PROMPTS.map((p) => (
                  <button
                    key={p.text}
                    onClick={() => handleQuickPrompt(p.text)}
                    className="text-[11px] px-2.5 py-1 rounded-full bg-[#E6F0F7] text-[#085489] hover:bg-[#d4e5f1] border border-[#b8d4e8] transition-colors"
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="border-t border-slate-200 p-3 bg-white">
            {file && (
              <div className="mb-2 flex items-center justify-between bg-[#E6F0F7] border border-[#b8d4e8] rounded-md px-2 py-1 text-[11px]">
                <span className="text-[#085489] truncate">📎 {file.name}</span>
                <button
                  onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ''; }}
                  className="text-[#085489] hover:text-[#063e6b] ml-2 font-bold"
                >
                  ×
                </button>
              </div>
            )}
            <div className="flex items-center gap-2">
              <label className="cursor-pointer h-9 w-9 flex items-center justify-center rounded-md border border-slate-200 hover:bg-slate-50 text-slate-500" title="Attach file">
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                </svg>
                <input
                  type="file"
                  ref={fileRef}
                  onChange={(e) => setFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)}
                  className="hidden"
                />
              </label>
              <input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask me anything..."
                disabled={loading}
                className="flex-1 h-9 rounded-md border border-slate-200 px-3 text-xs outline-none focus:border-[#0A6AAC] disabled:opacity-50"
              />
              <button
                onClick={handleSend}
                disabled={!canSend || loading}
                className="h-9 px-4 rounded-md bg-[#0A6AAC] hover:bg-[#085489] text-white text-xs font-semibold disabled:opacity-40 transition-colors"
                title="Send (Enter)"
              >
                Send
              </button>
            </div>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setOpen(true)}
          className="group h-14 w-14 rounded-full bg-gradient-to-br from-[#0A6AAC] to-[#085489] hover:from-[#085489] hover:to-[#063e6b] text-white shadow-2xl flex items-center justify-center transition-all hover:scale-110"
          title="Open Aquamaris Copilot (⌘K)"
        >
          <span className="text-2xl">🤖</span>
          <span className="absolute -top-1 -right-1 h-3 w-3 bg-amber-400 border-2 border-white rounded-full animate-pulse" />
        </button>
      )}
    </div>
  );
};

// ─── Sub-components ──────────────────────────────────────────────────

const MessageBubble: React.FC<{ msg: ChatMessage; onResultClick: (link?: string) => void }> = ({ msg, onResultClick }) => {
  const isUser = msg.role === 'user';
  return (
    <div className={isUser ? 'text-right' : 'text-left'}>
      <div
        className={[
          'inline-block max-w-[90%] rounded-2xl px-3 py-2 text-xs shadow-sm',
          isUser
            ? 'bg-[#0A6AAC] text-white rounded-tr-sm'
            : 'bg-white text-slate-700 border border-slate-200 rounded-tl-sm',
        ].join(' ')}
      >
        <div className="whitespace-pre-wrap leading-relaxed">{msg.text}</div>

        {/* Stats cards */}
        {msg.cards && msg.cards.length > 0 && (
          <div className="mt-2 grid grid-cols-2 gap-1.5">
            {msg.cards.map((c, i) => (
              <div key={i} className="bg-slate-50 border border-slate-200 rounded-md px-2 py-1.5 text-left">
                <div className="text-[9px] text-slate-400 uppercase tracking-wide truncate">{c.label}</div>
                <div className="text-sm font-bold text-slate-800 leading-tight">{c.value}</div>
                {c.sub && <div className="text-[9px] text-slate-500 truncate">{c.sub}</div>}
              </div>
            ))}
          </div>
        )}

        {/* Breakdown rows */}
        {msg.breakdown && msg.breakdown.length > 0 && (
          <div className="mt-2 space-y-1">
            {msg.breakdown.map((b, i) => (
              <div key={i} className="flex items-center justify-between text-[10px] bg-slate-50 border border-slate-200 rounded px-2 py-1">
                <span className="font-semibold capitalize">{b.label.replace(/_/g, ' ')}</span>
                <span className="text-slate-600">
                  {b.count != null && `${b.count}`}{b.outstanding && ` · ${b.outstanding}`}{b.total && ` · ${b.total}`}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Search results */}
        {msg.results && msg.results.length > 0 && (
          <div className="mt-2 space-y-1.5">
            {msg.results.map((item) => (
              <button
                key={`${item.type}-${item.id}`}
                onClick={() => onResultClick(item.link)}
                className="w-full text-left text-[11px] px-2 py-1.5 rounded-md border border-slate-200 bg-white hover:bg-[#E6F0F7] hover:border-[#0A6AAC] transition-colors group"
              >
                <div className="flex items-start gap-1.5">
                  <span className="text-sm leading-none mt-0.5">{TYPE_ICONS[item.type] || '🔹'}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="font-semibold text-slate-800 truncate group-hover:text-[#085489]">{item.title}</span>
                      {item.badge && (
                        <span className="text-[8px] uppercase tracking-wide bg-slate-100 text-slate-600 border border-slate-200 px-1 rounded">{item.badge}</span>
                      )}
                    </div>
                    {item.subtitle && <div className="text-[10px] text-slate-500 truncate">{item.subtitle}</div>}
                  </div>
                  <span className="text-slate-300 group-hover:text-[#0A6AAC]">→</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
      <div className={`text-[9px] text-slate-400 mt-0.5 ${isUser ? 'pr-1' : 'pl-1'}`}>
        {new Date(msg.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
      </div>
    </div>
  );
};
